import { useCallback, useMemo, useRef, useState } from 'react';
import { useRouter } from 'expo-router';
import { useTranslation } from 'react-i18next';
import { CompanyFormFields } from './CompanyFormFields.web';
import { useToast } from '../../hooks/useToast';
import { useWorkspace } from '../../providers/useWorkspace';
import { fromWebFile, uploadImageAsset } from '../../lib/image-upload';
import {
  ensureCompanyWebsite,
  getCompanyFormStatus,
  type CompanyFormValues,
  toCompanySlug,
} from '../../lib/company-form';
import { getWorkspaceChatHref } from '../../lib/workspace-routes';
import { WebDialog } from '../common/WebDialog.web';

const EMPTY_VALUES: CompanyFormValues = {
  name: '',
  slug: '',
  industry: '',
  employeeCount: '',
  location: '',
  website: '',
};

export function CreateCompanyScreen() {
  const { t } = useTranslation(['auth', 'common']);
  const router = useRouter();
  const toast = useToast();
  const fileRef = useRef<HTMLInputElement>(null);
  const { activeWorkspace, createCompanyWorkspace, isCreatingCompany } = useWorkspace();

  const [values, setValues] = useState<CompanyFormValues>(EMPTY_VALUES);
  const [logoUrl, setLogoUrl] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);

  const closeHref = useMemo(
    () => getWorkspaceChatHref(activeWorkspace),
    [activeWorkspace],
  );

  const closeDialog = useCallback(() => {
    if (typeof window !== 'undefined' && window.history.length > 1) {
      router.back();
      return;
    }

    router.replace(closeHref as never);
  }, [closeHref, router]);

  // Escape key handled by WebDialog

  const formStatus = useMemo(() => getCompanyFormStatus(values), [values]);
  const canSubmit = formStatus.isValid && !isCreatingCompany && !isUploadingLogo;

  const updateValues = (patch: Partial<CompanyFormValues>) => {
    setValues((previous) => {
      const next = { ...previous, ...patch };
      if (!slugTouched && patch.name !== undefined) {
        next.slug = toCompanySlug(patch.name);
      }
      return next;
    });
  };

  const handleSubmit = async () => {
    try {
      const result = await createCompanyWorkspace({
        name: values.name.trim(),
        slug: formStatus.normalizedSlug,
        industry: values.industry.trim(),
        employeeCount: values.employeeCount.trim(),
        location: values.location.trim(),
        website: ensureCompanyWebsite(values.website),
        logo: logoUrl || undefined,
      });

      router.replace(getWorkspaceChatHref(result.workspace) as never);
    } catch (error) {
      toast.error(
        t('onboarding.companyInfo.saveFailed', {
          ns: 'auth',
          defaultValue: 'Could not save company information',
        }),
        error instanceof Error ? error.message : 'Please try again.',
      );
    }
  };

  const submitFooter = (
    <button
      onClick={handleSubmit}
      disabled={!canSubmit}
      style={{
        width: '100%', height: 48, borderRadius: 9999, border: 'none',
        cursor: canSubmit ? 'pointer' : 'default',
        backgroundColor: canSubmit ? 'var(--text-primary)' : 'var(--alpha-05)',
        color: canSubmit ? 'var(--bg)' : 'var(--text-tertiary)',
        fontSize: 15, fontWeight: 600, fontFamily: 'var(--font-sans)',
        transition: 'background-color 150ms, color 150ms, opacity 150ms',
        opacity: canSubmit ? 1 : 0.7,
      }}
    >
      {isCreatingCompany
        ? t('onboarding.companyInfo.saving', { ns: 'auth', defaultValue: 'Saving...' })
        : t('onboarding.continue', { ns: 'auth', defaultValue: 'Continue' })}
    </button>
  );

  return (
    <WebDialog
      title={t('labels.addWorkspace', { ns: 'common', defaultValue: 'Add workspace' })}
      onClose={closeDialog}
      maxWidth={650}
      footer={submitFooter}
    >
      <input
        ref={fileRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        style={{ display: 'none' }}
        onChange={(event) => {
          const file = event.target.files?.[0] ?? null;
          if (file) {
            void (async () => {
              const asset = fromWebFile(file);
              try {
                setIsUploadingLogo(true);
                const uploadedUrl = await uploadImageAsset(asset, {
                  kind: 'company-logo',
                  fileName: asset.name,
                  mimeType: asset.type,
                });
                setLogoUrl(uploadedUrl);
              } catch (error) {
                toast.error(
                  t('onboarding.companyInfo.saveFailed', {
                    ns: 'auth',
                    defaultValue: 'Could not save company information',
                  }),
                  error instanceof Error ? error.message : 'Please try again.',
                );
              } finally {
                URL.revokeObjectURL(asset.uri);
                setIsUploadingLogo(false);
              }
            })();
          }
          event.currentTarget.value = '';
        }}
      />

      <div style={{ paddingTop: 8 }}>
        <h2 style={{ fontSize: 24, fontWeight: 600, color: 'var(--text-primary)', margin: '0 0 4px', fontFamily: 'var(--font-sans)' }}>
          {t('onboarding.companyInfo.title', { ns: 'auth', defaultValue: 'Company information' })}
        </h2>
        <p style={{ fontSize: 14, color: 'var(--text-secondary)', margin: '0 0 24px' }}>
          {t('onboarding.companyInfo.subtitle', { ns: 'auth', defaultValue: "Set up your company's career page with basic information." })}
        </p>

        <CompanyFormFields
              values={values}
              onChange={updateValues}
              onSlugTouched={() => setSlugTouched(true)}
              selectedLogo={logoUrl}
              onPickLogo={() => fileRef.current?.click()}
            />
      </div>
    </WebDialog>
  );
}
